import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, API_BASE, formatApiError, fmtCurrency, fmtInt, fmtPct, fmtYears, tokenStore } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { termShort } from "@/lib/terms";
import { useAuth } from "@/contexts/AuthContext";
import AppLayout from "@/components/AppLayout";
import ChartDescriptionEditor from "@/components/ChartDescriptionEditor";
import RiskComparisonTable from "@/components/RiskComparisonTable";
import { ArrowLeft, Layers, FileText } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend, ReferenceLine,
} from "recharts";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";
import { saveOrDownload } from "@/lib/downloadFolder";
import { applyDiagonalWatermark, hexToRgb } from "@/lib/pdfBranding";

const COLORS = ["#0047FF", "#FF5900", "#00D05A", "#FFD700", "#7C3AED", "#EA580C"];

export default function ComparisonPage() {
  const t = useT();
  const { clubId } = useParams();
  const nav = useNavigate();
  const [scenarios, setScenarios] = useState([]);
  const [selected, setSelected] = useState([]);
  const [results, setResults] = useState([]);
  const [club, setClub] = useState(null);
  const [historicals, setHistoricals] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [risk, setRisk] = useState(null);
  const [savingPdf, setSavingPdf] = useState(false);
  const { user } = useAuth();
  const hasNpvAccess = user && (user.tier === "TRIAL" || user.tier === "PREMIUM" || user.role === "admin");

  const loadClub = () => api.get(`/clubs/${clubId}`).then(({ data }) => setClub(data)).catch(() => setClub(null));

  useEffect(() => {
    api.get(`/clubs/${clubId}/scenarios`).then(({ data }) => {
      setScenarios(data);
      const initial = data.slice(0, Math.min(3, data.length)).map((s) => s.id);
      setSelected(initial);
    });
    loadClub();
    api.get(`/clubs/${clubId}/historicals`)
      .then(({ data }) => setHistoricals(data || []))
      .catch(() => setHistoricals([]));
    api.get(`/clubs/${clubId}/budgets`)
      .then(({ data }) => setBudgets(data || []))
      .catch(() => setBudgets([]));
    // Risicovergelijking: laatste Monte Carlo-run per scenario (alleen voor wie MC mag draaien).
    api.get(`/clubs/${clubId}/monte-carlo/risk`)
      .then(({ data }) => setRisk(data.risk || {}))
      .catch(() => setRisk(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId]);

  const saveCompareDescription = async (txt) => {
    const { data: updated } = await api.patch(`/clubs/${clubId}/compare-description`, { description: txt });
    setClub((c) => c ? { ...c, compare_description: updated.compare_description || "" } : updated);
  };

  useEffect(() => {
    if (selected.length === 0) { setResults([]); return; }
    api.post(`/clubs/${clubId}/compare`, { scenario_ids: selected })
      .then(({ data }) => setResults(data.results))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [selected, clubId]);

  // Laatste afgesloten boekjaar → dat sluitsaldo is de "startpositie" waarop
  // de scenario's verder bouwen. Alle scenario-projecties tellen we hier
  // bovenop zodat de grafiek de échte cashpositie toont, niet slechts de
  // cumulatieve delta vanaf 0.
  const lastClosedHist = useMemo(() => {
    if (!historicals.length) return null;
    return [...historicals].sort((a, b) => (b.year || 0) - (a.year || 0))[0];
  }, [historicals]);
  const openingCash = Number(lastClosedHist?.cash_bank_balance || 0);

  // Multi-series data: elk scenario levert 3 lijnen — kaspositie, investeringen,
  // leningen. Naming-conventie:
  //   `${name} · Kaspositie` (cashpositie op 31/12 van dat jaar)
  //   `${name} · Investeringen` (cumulatieve investeringen tot en met dat jaar)
  //   `${name} · Leningen` (cumulatieve nieuwe leningen tot en met dat jaar)
  // X-axis: `actual_year` uit compute.py — geen index nummer meer.
  const combinedChart = useMemo(() => {
    if (results.length === 0) return [];
    // Verzamel alle unieke jaartallen (union) + Y-1 als startpunt voor de kaspositie.
    const scenarioYears = new Set();
    results.forEach((r) => (r.years || []).forEach((y) => scenarioYears.add(y.actual_year)));
    const startYear = scenarioYears.size ? Math.min(...scenarioYears) : (lastClosedHist?.year || 0);
    const priorYear = startYear - 1;
    scenarioYears.add(priorYear);
    const orderedYears = [...scenarioYears].sort((a, b) => a - b);

    return orderedYears.map((actualYear) => {
      const row = { year: actualYear };
      results.forEach((r) => {
        const nm = r.scenario.name;
        // Kaspositie: opening_cash + cumulative_cashflow tot en met dit jaar.
        if (actualYear < startYear) {
          // Y-1 rij: alleen het historische sluitsaldo, geen invest./lening
          row[`${nm} · Kaspositie`] = openingCash;
        } else {
          const y = (r.years || []).find((yy) => yy.actual_year === actualYear);
          if (y) {
            row[`${nm} · Kaspositie`] = openingCash + (Number(y.cumulative) || 0);
          }
          // Cumulatieve investeringen + leningen tot en met dit jaar.
          let cumCapex = 0;
          let cumLoan = 0;
          for (const yy of (r.years || [])) {
            if (yy.actual_year <= actualYear) {
              cumCapex += Number(yy.future_capex || 0);
              cumLoan  += Number(yy.loan_disbursement || 0);
            }
          }
          row[`${nm} · Investeringen`] = cumCapex;
          row[`${nm} · Leningen`] = cumLoan;
        }
      });
      return row;
    });
  }, [results, lastClosedHist, openingCash]);

  const baselineLabel = (r) => {
    const src = r?.baseline_source;
    const yr  = r?.baseline_actual_year;
    if (src === "budget" && yr) return `${t("Begroting")} ${yr}`;
    if (src === "last_historical" && yr) return `${t("Resultaat")} ${yr}`;
    if (src === "budget") return t("Begroting");
    if (src === "last_historical") return t("Laatste boekjaar");
    return t("Handmatige input");
  };

  /** Deep-link naar het juiste tab op de ClubPage — Begroting-tab bij budget,
   * Voorgaande-jaren-tab bij historical. Return `null` als er geen zinnige
   * bestemming is (bv. "Handmatige input"). */
  const baselineTarget = (r) => {
    const src = r?.baseline_source;
    if (src === "budget") return `/clubs/${clubId}?tab=budget`;
    if (src === "last_historical") return `/clubs/${clubId}?tab=historicals`;
    return null;
  };

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const downloadBundlePdf = async () => {
    if (results.length === 0) return;
    setSavingPdf(true);
    try {
      const doc = new jsPDF({ orientation: "landscape" });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const brandRgb = hexToRgb(club?.brand_color || "#0047FF");
      // Cover
      doc.setFillColor(10, 10, 10);
      doc.rect(0, 0, pageW, 60, "F");
      let hasLogoOnCover = false;
      if (club?.logo_data_uri) {
        try {
          doc.addImage(club.logo_data_uri, "AUTO", 20, 18, 24, 24, undefined, "FAST");
          hasLogoOnCover = true;
        } catch (err) {
          console.warn("Bundle cover logo failed to render:", err);
          doc.setFillColor(brandRgb[0], brandRgb[1], brandRgb[2]);
          doc.rect(20, 20, 10, 22, "F");
        }
      } else {
        doc.setFillColor(brandRgb[0], brandRgb[1], brandRgb[2]);
        doc.rect(20, 20, 10, 22, "F");
      }
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(11);
      doc.text("MEERJARENPLAN ONLINE · VERGELIJKINGSRAPPORT", hasLogoOnCover ? 50 : 34, 28);
      doc.setFontSize(28);
      doc.text(club?.name || "Club", hasLogoOnCover ? 50 : 34, 40);
      doc.setTextColor(10, 10, 10);
      doc.setFontSize(24);
      doc.text(t("Multi-scenario vergelijking"), 20, 80);
      doc.setFontSize(10);
      doc.setTextColor(120);
      doc.text(t("Scenario's") + `: ${results.length}`, 20, 90);
      const today = new Date().toLocaleDateString("nl-NL", { day: "2-digit", month: "long", year: "numeric" });
      doc.text(t("Rapportdatum") + `: ${today}`, 20, 96);

      // Summary table across scenarios
      const bodyRows = [];
      const push = (label, getter) => bodyRows.push([label, ...results.map(getter)]);
      push(t("Type"), (r) => (r.scenario.kind || "—").toUpperCase());
      if (hasNpvAccess) {
        push(termShort("npv"), (r) => fmtCurrency(r.npv));
        push(termShort("irr"), (r) => fmtPct(r.irr));
        push(t("Terugverdientijd"), (r) => fmtYears(r.payback_years));
        push(termShort("dpayback"), (r) => fmtYears(r.discounted_payback_years));
      }
      push(t("Capex"), (r) => fmtCurrency(r.capex_total));
      push(t("Omzet/jaar"), (r) => fmtCurrency(r.annual_revenue_base));
      push(t("Kosten/jaar"), (r) => fmtCurrency(r.annual_opex_base));
      push(t("Horizon"), (r) => `${r.scenario.horizon_years} ${t("jr")}`);
      push(t("Basis"), baselineLabel);
      // Risicovergelijking (Monte Carlo) — alleen als minstens één scenario gesimuleerd is.
      const rk = (r) => risk?.[r.scenario.id] || null;
      if (hasNpvAccess && results.some(rk)) {
        const pctR = (v) => (v == null ? "—" : `${(v * 100).toFixed(0)}%`);
        push(t("Monte Carlo"), (r) => (rk(r) ? `${rk(r).runs} runs` : t("niet gesimuleerd")));
        push(t("Kans kastekort"), (r) => (rk(r) ? pctR(rk(r).prob_cash_shortfall) : "—"));
        if (results.some((r) => rk(r)?.budget)) {
          push(t("Ondergrens eigen kas"), (r) => (rk(r)?.budget ? fmtCurrency(rk(r).budget.cash_floor) : "—"));
          push(t("Kans extra financiering"), (r) => (rk(r)?.budget ? pctR(rk(r).budget.prob_financing) : "—"));
          push(t("Meerkosten P90"), (r) => (rk(r)?.budget ? fmtCurrency(rk(r).budget.cost_p90) : "—"));
          push(t("Kas onder ondergrens in"), (r) => (rk(r)?.budget ? (rk(r).budget.years_below_floor_p50.join(", ") || t("geen enkel jaar")) : "—"));
        }
      }
      autoTable(doc, {
        startY: 110,
        head: [[t("Metriek"), ...results.map((r) => r.scenario.name)]],
        body: bodyRows,
        styles: { fontSize: 9, halign: "right" },
        headStyles: { fontSize: 9, halign: "center", fillColor: [10, 10, 10] },
        columnStyles: { 0: { halign: "left", fontStyle: "bold", cellWidth: 60 } },
      });

      // Chart page — screenshot the on-screen combined chart.
      // Temporarily strip the `dark` class so html2canvas captures a light-mode
      // chart (dark cards would produce a near-black block in an otherwise
      // white PDF page).
      const chartEl = document.querySelector('[data-testid="compare-chart-panel"]');
      if (chartEl) {
        const html = document.documentElement;
        const wasDark = html.classList.contains("dark");
        if (wasDark) html.classList.remove("dark");
        try {
          const canvas = await html2canvas(chartEl, {
            backgroundColor: "#ffffff",
            scale: 2,
            logging: false,
            ignoreElements: (node) => node.getAttribute?.("data-pdf-ignore") === "1",
          });
          const img = canvas.toDataURL("image/png");
          const marginX = 14;
          const topY = 26;
          const maxW = pageW - 2 * marginX;
          const descText = (club?.compare_description || "").trim();
          const descWrapped = descText ? doc.splitTextToSize(descText, maxW - 4) : [];
          const descBlockH = descWrapped.length ? Math.min(50, 10 + descWrapped.length * 4.5) : 0;
          const maxH = pageH - topY - 14 - descBlockH;
          const aspect = canvas.width / canvas.height;
          let w = maxW;
          let h = w / aspect;
          if (h > maxH) { h = maxH; w = h * aspect; }
          doc.addPage();
          doc.setFontSize(9);
          doc.setTextColor(120);
          doc.text((t("Cumulatieve kasstroom over scenario's") || "").toUpperCase(), 14, 14);
          doc.setTextColor(0);
          doc.setFontSize(14);
          doc.text(t("Trajectvergelijking"), 14, 22);
          const x = marginX + (maxW - w) / 2;
          doc.addImage(img, "PNG", x, topY, w, h, undefined, "FAST");
          if (descWrapped.length) {
            const descY = topY + h + 6;
            doc.setDrawColor(brandRgb[0], brandRgb[1], brandRgb[2]);
            doc.setLineWidth(0.8);
            doc.line(marginX, descY - 3, marginX + 2, descY - 3);
            doc.setFontSize(9);
            doc.setTextColor(120);
            doc.text("TOELICHTING", marginX, descY);
            doc.setFontSize(10);
            doc.setTextColor(10, 10, 10);
            doc.text(descWrapped, marginX, descY + 5);
          }
        } catch (err) {
          console.warn("Compare chart capture failed:", err);
        } finally {
          if (wasDark) html.classList.add("dark");
        }
      }

      // ---- Vergelijkingstabel: Begroting Y-1 · Resultaat Y-1 · Begroting Y ----
      // Deze sectie geeft bestuur/accountant een compact drift-overzicht van de
      // laatste twee begrotingen naast het feitelijke resultaat, inclusief
      // subposten. Alleen renderen als er tenminste één begroting is.
      if (budgets.length > 0) {
        const budgetByYear = Object.fromEntries(budgets.map((b) => [b.year, b]));
        const histByYear = Object.fromEntries(historicals.map((h) => [h.year, h]));
        const latestYear = Math.max(...budgets.map((b) => b.year));
        const target = budgetByYear[latestYear];
        const prevYear = latestYear - 1;
        const prevBudget = budgetByYear[prevYear] || null;
        const prevActual = histByYear[prevYear] || null;
        if (target && (prevBudget || prevActual)) {
          doc.addPage();
          doc.setFillColor(brandRgb[0], brandRgb[1], brandRgb[2]);
          doc.rect(14, 10, 10, 8, "F");
          doc.setFontSize(9);
          doc.setTextColor(120);
          doc.text("VERGELIJKING · BEGROTING VS RESULTAAT", 28, 14);
          doc.setTextColor(10, 10, 10);
          doc.setFontSize(14);
          doc.text(t("Begroting {y} vs {p}").replace("{y}", String(latestYear)).replace("{p}", String(prevYear)), 28, 22);

          const bmRows = [
            { key: "revenue",          label: t("Omzet"),            sub: "revenue_breakdown" },
            { key: "other_income",     label: t("Overige baten"),    sub: "other_income_breakdown" },
            { key: "operating_costs",  label: t("Bedrijfskosten"),   sub: "opex_breakdown" },
            { key: "depreciation",     label: t("Afschrijvingen"),   sub: null },
            { key: "interest_paid",    label: t("Rentelasten"),      sub: null },
            { key: "interest_income",  label: t("Rentebaten"),       sub: null },
            { key: "cash_bank_balance", label: t("Kaspositie 31/12"), sub: null },
          ];

          const num = (o, k) => (o ? Number(o[k] || 0) : null);
          const drift = (base, next) => (base === null || next === null || !base ? null : ((next - base) / Math.abs(base)) * 100);
          const driftE = (base, next) => (base === null || next === null ? null : next - base);
          const fmtP = (p) => (p === null || Number.isNaN(p) ? "—" : `${p > 0 ? "+" : ""}${p.toFixed(1)}%`);
          const fmtE = (v) => (v === null ? "—" : fmtCurrency(v));
          const fmtDelta = (v) => {
            if (v === null) return "—";
            const s = v > 0 ? "+" : v < 0 ? "−" : "";
            return `${s}${fmtCurrency(Math.abs(v))}`;
          };
          const subUnion = (key) => {
            if (!key) return [];
            const s = new Set();
            for (const src of [prevBudget, prevActual, target]) {
              for (const it of (src?.[key] || [])) {
                const n = String(it?.name || "").trim();
                if (n) s.add(n);
              }
            }
            return [...s];
          };
          const subVal = (o, key, name) => {
            if (!o) return null;
            const hit = (o[key] || []).find((it) => String(it?.name || "").trim() === name);
            return hit ? Number(hit.amount || 0) : null;
          };

          const body = [];
          for (const { key, label, sub } of bmRows) {
            const bp = num(prevBudget, key);
            const ap = num(prevActual, key);
            const bt = num(target, key);
            body.push([label, fmtE(bp), fmtE(ap), fmtE(bt), fmtDelta(driftE(ap, bt)), fmtP(drift(ap, bt))]);
            for (const name of subUnion(sub)) {
              const spBp = subVal(prevBudget, sub, name);
              const spAp = subVal(prevActual, sub, name);
              const spBt = subVal(target, sub, name);
              body.push([`    ${name}`, fmtE(spBp), fmtE(spAp), fmtE(spBt), fmtDelta(driftE(spAp, spBt)), fmtP(drift(spAp, spBt))]);
            }
          }

          autoTable(doc, {
            startY: 30,
            head: [[t("Post"), `${t("Begroting")} ${prevYear}`, `${t("Resultaat")} ${prevYear}`, `${t("Begroting")} ${latestYear}`, t("Δ € vs resultaat"), t("Δ% vs resultaat")]],
            body,
            styles: { fontSize: 9, halign: "right" },
            headStyles: { fillColor: [10, 10, 10], halign: "center", fontSize: 9 },
            columnStyles: { 0: { halign: "left", fontStyle: "bold", cellWidth: 60 } },
            didParseCell: (data) => {
              if (data.section !== "body") return;
              // Sub-rijen (4-spaties inspring) → italic + muted.
              if (data.column.index === 0) {
                const raw = String(data.cell.raw || "");
                if (raw.startsWith("    ")) {
                  for (let i = 0; i < 6; i++) {
                    if (data.row.cells[i]) {
                      data.row.cells[i].styles.fontStyle = "italic";
                      data.row.cells[i].styles.textColor = [90, 90, 90];
                    }
                  }
                }
              }
              // Δ%-kolom rood wanneer |drift| ≥ 20%.
              if (data.column.index === 5) {
                const raw = String(data.cell.raw || "");
                const m = raw.match(/([+-]?\d+(\.\d+)?)/);
                if (m) {
                  const val = Math.abs(parseFloat(m[1]));
                  if (val >= 20) {
                    data.cell.styles.textColor = [200, 30, 30];
                    data.cell.styles.fontStyle = "bold";
                  }
                }
              }
            },
          });

          // Toelichting per begroting-jaar onder de tabel.
          let notesY = (doc.lastAutoTable?.finalY || 30) + 8;
          const addBudgetNotes = (label, notesText) => {
            if (!notesText || !notesText.trim()) return;
            const lines = doc.splitTextToSize(notesText.trim(), pageW - 28);
            if (notesY + 6 + lines.length * 4 > pageH - 14) {
              doc.addPage();
              notesY = 20;
            }
            doc.setFontSize(9);
            doc.setTextColor(90, 90, 90);
            doc.text(label, 14, notesY);
            doc.setFontSize(9);
            doc.setTextColor(0, 0, 0);
            doc.text(lines, 14, notesY + 4);
            notesY += 4 + lines.length * 4 + 6;
          };
          if (prevBudget?.notes) addBudgetNotes(`${t("Toelichting")} · ${t("Begroting")} ${prevYear}:`, prevBudget.notes);
          if (target?.notes) addBudgetNotes(`${t("Toelichting")} · ${t("Begroting")} ${latestYear}:`, target.notes);
        }
      }

      // ---- Historische kerncijfers (max 5 laatste jaren) ----
      // Compact transposed overzicht — posten als rijen, jaren als kolommen.
      // Zichtbare rentelasten + rentebaten regels zodat het jaarverslag-formaat
      // consistent is (baten en lasten expliciet gescheiden).
      if (historicals && historicals.length > 0) {
        const sorted = [...historicals].sort((a, b) => (a.year || 0) - (b.year || 0));
        const shownYears = sorted.slice(-5); // laatste 5 jaren
        const yearCols = shownYears.map((h) => h.year);
        doc.addPage();
        doc.setFillColor(brandRgb[0], brandRgb[1], brandRgb[2]);
        doc.rect(14, 10, 10, 8, "F");
        doc.setFontSize(9);
        doc.setTextColor(120);
        doc.text("HISTORISCHE KERNCIJFERS · JAARVERSLAG-FORMAAT", 28, 14);
        doc.setTextColor(10, 10, 10);
        doc.setFontSize(14);
        doc.text(t("Historische kerncijfers"), 28, 22);

        const nettoRes = (h) =>
          Number(h.revenue || 0)
          + Number(h.other_income || 0)
          - Number(h.operating_costs || 0)
          - Number(h.depreciation || 0)
          - Number(h.interest_paid || 0)
          + Number(h.interest_income || 0);

        const rowSpecs = [
          [t("Omzet"),               (h) => h.revenue,             false],
          [t("Overige baten"),       (h) => h.other_income || 0,   false],
          [t("Bedrijfskosten"),      (h) => -(h.operating_costs || 0), false],
          [t("Afschrijvingen"),      (h) => -(h.depreciation || 0),false],
          [t("Rentelasten"),         (h) => -(h.interest_paid || 0), false],
          [t("Rentebaten"),          (h) => h.interest_income || 0, false],
          [t("Netto resultaat"),     nettoRes,                     true ],
          [t("Kas + bank 31/12"),    (h) => h.cash_bank_balance || 0, true ],
        ];
        const histHead = [[t("Post"), ...yearCols.map(String)]];
        const histBody = rowSpecs.map(([label, getter]) => [
          label, ...shownYears.map((h) => fmtCurrency(getter(h))),
        ]);
        autoTable(doc, {
          startY: 30,
          head: histHead,
          body: histBody,
          styles: { fontSize: 9, halign: "right" },
          headStyles: { fontSize: 9, halign: "center", fillColor: [10, 10, 10] },
          columnStyles: { 0: { halign: "left", fontStyle: "bold", cellWidth: 62 } },
          didParseCell: (h) => {
            const spec = rowSpecs[h.row.index];
            if (spec && spec[2] && h.section === "body") {
              h.cell.styles.fontStyle = "bold";
              h.cell.styles.fillColor = [235, 240, 255];
            }
          },
        });
        // Voetnoot uitleg netto-formule
        const finalY = doc.lastAutoTable.finalY + 6;
        doc.setFontSize(8);
        doc.setTextColor(120);
        doc.text(t("Netto resultaat = Omzet + Overige baten − Bedrijfskosten − Afschrijvingen − Rentelasten + Rentebaten."),
          14, finalY);
        doc.setTextColor(10, 10, 10);
      }

      // ---- Per-scenario detail sections (Bundel-mode) ----
      // For each selected scenario add: cover strip, metric summary, yearly projection
      // table and (if present) reserves warnings. This gives boards one printable
      // artefact instead of chasing individual scenario PDFs.
      for (const r of results) {
        const sc = r.scenario;
        const years = r.years || [];
        doc.addPage();
        doc.setFillColor(10, 10, 10);
        doc.rect(0, 0, pageW, 22, "F");
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(9);
        doc.text("SCENARIO", 14, 10);
        doc.setFontSize(14);
        doc.text(`${sc.name}  (${(sc.kind || "").toUpperCase()})`, 14, 18);
        doc.setTextColor(10, 10, 10);

        // Optional scenario summary at the top
        const summary = (sc.scenario_summary || "").trim();
        let cursorY = 30;
        if (summary) {
          const wrapped = doc.splitTextToSize(summary, pageW - 28);
          doc.setFontSize(9);
          doc.setTextColor(120);
          doc.text("SAMENVATTING", 14, cursorY);
          doc.setTextColor(10, 10, 10);
          doc.setFontSize(10);
          doc.text(wrapped, 14, cursorY + 5);
          cursorY += 8 + wrapped.length * 4.5;
        }

        // Metric summary
        const metricRows = [];
        if (hasNpvAccess) {
          metricRows.push([termShort("npv"), fmtCurrency(r.npv)]);
          metricRows.push([termShort("irr"), fmtPct(r.irr)]);
          metricRows.push([t("Terugverdientijd"), fmtYears(r.payback_years)]);
        }
        metricRows.push([t("Basisomzet / jaar"), fmtCurrency(r.annual_revenue_base)]);
        metricRows.push([t("Basiskosten / jaar"), fmtCurrency(r.annual_opex_base)]);
        metricRows.push([t("Horizon"), `${sc.horizon_years} ${t("jr")}`]);
        metricRows.push([t("Inflatie"), fmtPct(sc.inflation_rate)]);
        metricRows.push([t("Disconteringsvoet"), fmtPct(sc.discount_rate)]);
        autoTable(doc, {
          startY: cursorY + 4,
          head: [[t("Kernmetrieken"), t("Waarde")]],
          body: metricRows,
          styles: { fontSize: 9 },
          headStyles: { fillColor: [10, 10, 10], fontSize: 9 },
          columnStyles: { 1: { halign: "right", cellWidth: 60 } },
        });

        // Yearly projection (transposed like the on-screen table)
        const projRows = [
          [t("Omzet"), (y) => y.revenue],
          [t("Tot. kosten - afschr."), (y) => y.total_costs_ex_dep ?? ((y.opex || 0) + (y.interest || 0) + (y.principal || 0) + (y.tax || 0))],
          ["EBITDA", (y) => y.ebitda],
          [t("Afschr."), (y) => y.depreciation],
          [t("Kasstroom"), (y) => y.cash_flow],
          [t("Cumulatief"), (y) => y.cash_position ?? y.cumulative ?? 0],
        ];
        const head = [[t("Post"), ...years.map((y) => `${y.actual_year}\nY${y.year}`)]];
        const body = projRows.map(([label, getter]) => [label, ...years.map((y) => fmtInt(getter(y)))]);
        autoTable(doc, {
          startY: doc.lastAutoTable.finalY + 6,
          head, body,
          styles: { fontSize: 7, halign: "right" },
          headStyles: { fontSize: 7, halign: "center", fillColor: [10, 10, 10] },
          columnStyles: { 0: { halign: "left", fontStyle: "bold", cellWidth: 40 } },
        });

        // Reserves warnings, if any.
        const warnings = r.reserves_projection?.warnings || [];
        if (warnings.length) {
          const wy = (doc.lastAutoTable?.finalY || cursorY) + 8;
          doc.setDrawColor(220, 38, 38);
          doc.setFillColor(254, 226, 226);
          doc.rect(14, wy, pageW - 28, 6 + warnings.length * 5, "FD");
          doc.setFontSize(9);
          doc.setTextColor(153, 27, 27);
          doc.text(t("Reserves-waarschuwingen"), 16, wy + 5);
          doc.setTextColor(10, 10, 10);
          doc.setFontSize(9);
          warnings.forEach((w, i) => doc.text(`• ${w}`, 16, wy + 10 + i * 5));
        }
      }

      // Apply PROEF watermark for TRIAL users on every page.
      if (user?.tier === "TRIAL") {
        applyDiagonalWatermark(doc, "PROEF");
      }

      const filename = `${(club?.name || "club").replace(/[\\/:*?"<>|]/g, "-")}-vergelijking.pdf`;
      const blob = doc.output("blob");
      const wrote = await saveOrDownload(blob, filename, clubId);
      if (wrote) toast.success(t("Opgeslagen in map ‘{name}’").replace("{name}", filename));
    } catch (err) {
      toast.error(formatApiError(err?.message));
    } finally {
      setSavingPdf(false);
    }
  };

  return (
    <AppLayout>
      <button onClick={() => nav(`/clubs/${clubId}`)} data-testid="back-club"
        className="text-sm inline-flex items-center gap-1 text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="w-4 h-4" /> {t("Terug naar club")}
      </button>

      <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
        <div>
          <div className="stat-label">{t("Multi-scenario vergelijking")}</div>
          <h1 className="font-display font-black text-5xl md:text-6xl uppercase tracking-tighter mt-1 inline-flex items-center gap-3">
            <Layers className="w-10 h-10" /> {t("Vergelijken")}
          </h1>
        </div>
        <button
          onClick={downloadBundlePdf}
          disabled={results.length === 0 || savingPdf}
          data-testid="compare-download-pdf"
          className="btn-brutal bg-primary text-primary-foreground px-4 py-2 font-display uppercase font-bold inline-flex items-center gap-2 disabled:opacity-50"
        >
          <FileText className="w-4 h-4" /> {savingPdf ? t("Bezig…") : t("Gebundelde PDF")}
        </button>
      </div>

      <div className="bg-card border-2 border-foreground p-5 heavy-shadow mb-8">
        <div className="stat-label mb-3">{t("Selecteer scenario's om te vergelijken")}</div>
        <div className="flex flex-wrap gap-2">
          {scenarios.map((s) => (
            <button key={s.id} onClick={() => toggle(s.id)} data-testid={`toggle-${s.id}`}
              className={`btn-brutal px-3 py-2 font-display uppercase font-bold text-sm ${
                selected.includes(s.id) ? "bg-primary text-primary-foreground" : "bg-background text-foreground"
              }`}>
              {s.name} <span className="opacity-70 ml-1 text-xs">({s.kind})</span>
            </button>
          ))}
        </div>
      </div>

      {results.length === 0 ? (
        <div className="border-2 border-dashed border-foreground/40 p-10 text-center text-muted-foreground">
          {t("Selecteer minstens één scenario om te vergelijken.")}
        </div>
      ) : (
        <>
          <div className={`grid gap-6 mb-8 ${results.length === 1 ? "" : results.length === 2 ? "md:grid-cols-2" : "md:grid-cols-3"}`} data-testid="compare-cards">
            {results.map((r, i) => (
              <div key={r.scenario.id}
                className={`bg-card border-2 heavy-shadow p-6 ${r.scenario.kind === "base" ? "border-primary" : "border-foreground"}`}>
                <div className="flex items-center justify-between">
                  <span className={`stat-label ${r.scenario.kind === 'best' ? 'text-positive' : r.scenario.kind === 'worst' ? 'text-destructive' : 'text-primary'}`}>{r.scenario.kind?.toUpperCase()}</span>
                  <span className="w-3 h-3" style={{ background: COLORS[i % COLORS.length] }} />
                </div>
                <h3 className="font-display font-black text-3xl uppercase tracking-tight mt-1">{r.scenario.name}</h3>
                <dl className="mt-4 space-y-2">
                  {hasNpvAccess && (
                    <>
                      <Row k={termShort("npv")} v={fmtCurrency(r.npv)} highlight={r.npv >= 0 ? "positive" : "destructive"} />
                      <Row k={termShort("irr")} v={fmtPct(r.irr)} />
                      <Row k={t("Terugverdientijd")} v={fmtYears(r.payback_years)} />
                      <Row k={termShort("dpayback")} v={fmtYears(r.discounted_payback_years)} />
                    </>
                  )}
                  <Row k={t("Capex")} v={fmtCurrency(r.capex_total)} />
                  <Row k={t("Omzet/jaar")} v={fmtCurrency(r.annual_revenue_base)} />
                  <Row k={t("Kosten/jaar")} v={fmtCurrency(r.annual_opex_base)} />
                  <Row k={t("Horizon")} v={`${r.scenario.horizon_years} ${t("jr")}`} />
                  <Row k={t("Basis")} v={
                    baselineTarget(r) ? (
                      <Link
                        to={baselineTarget(r)}
                        className="underline underline-offset-2 hover:text-primary"
                        data-testid={`baseline-link-${r.scenario.id}`}
                      >{baselineLabel(r)}</Link>
                    ) : baselineLabel(r)
                  } />
                </dl>
              </div>
            ))}
          </div>

          {hasNpvAccess && risk && <RiskComparisonTable results={results} risk={risk} clubId={clubId} />}

          <div className="bg-card border-2 border-foreground p-6 heavy-shadow" data-testid="compare-chart-panel">
            <div className="stat-label">
              {t("Kaspositie · investeringen · nieuwe leningen per scenario")}
              {lastClosedHist ? " · " + t("start = kas 31/12 laatste boekjaar {y}").replace("{y}", String(lastClosedHist.year)) : ""}
            </div>
            <h2 className="font-display font-black text-2xl uppercase tracking-tight mb-4">{t("Trajectvergelijking")}</h2>
            <div className="h-[440px]">
              <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 500, height: 440 }}>
                <LineChart data={combinedChart}>
                  <CartesianGrid stroke="#e2e8f0" />
                  <XAxis dataKey="year" />
                  <YAxis tickFormatter={(v) => fmtCurrency(v)} width={80} />
                  <Tooltip formatter={(v) => fmtCurrency(v)} />
                  <Legend />
                  <ReferenceLine y={0} stroke="#0A0A0A" strokeWidth={2} />
                  {results.map((r, i) => {
                    const color = COLORS[i % COLORS.length];
                    const nm = r.scenario.name;
                    return [
                      <Line key={`${r.scenario.id}-cash`} type="monotone" dataKey={`${nm} · Kaspositie`}
                        stroke={color} strokeWidth={3} dot={false} />,
                      <Line key={`${r.scenario.id}-capex`} type="monotone" dataKey={`${nm} · Investeringen`}
                        stroke={color} strokeWidth={2} strokeDasharray="5 5" dot={false} />,
                      <Line key={`${r.scenario.id}-loan`} type="monotone" dataKey={`${nm} · Leningen`}
                        stroke={color} strokeWidth={2} strokeDasharray="2 4" dot={false} />,
                    ];
                  })}
                </LineChart>
              </ResponsiveContainer>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {t("Lijn-stijl per scenario: volle lijn = kaspositie, streepje-streepje = cumulatieve investeringen, kort-stipje = cumulatieve nieuwe leningen.")}
            </p>
            <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1" data-testid="compare-baselines-caption">
              {results.map((r, i) => {
                const target = baselineTarget(r);
                const labelEl = target ? (
                  <Link to={target} className="font-mono-num text-foreground underline underline-offset-2 hover:text-primary">
                    {baselineLabel(r)}
                  </Link>
                ) : (
                  <span className="font-mono-num text-foreground">{baselineLabel(r)}</span>
                );
                return (
                  <div key={r.scenario.id} className="text-xs flex items-center gap-2">
                    <span className="inline-block w-3 h-3" style={{ background: COLORS[i % COLORS.length] }} />
                    <span className="font-bold">{r.scenario.name}</span>
                    <span className="text-muted-foreground">— {t("Basis")}: {labelEl}</span>
                  </div>
                );
              })}
            </div>
            <ChartDescriptionEditor
              value={club?.compare_description || ""}
              onSave={saveCompareDescription}
              dialogTitle={t("Beschrijving bij Trajectvergelijking")}
              testid="chart-desc-compare"
            />
          </div>
        </>
      )}
    </AppLayout>
  );
}

function Row({ k, v, highlight }) {
  const cls = highlight === "positive" ? "text-positive"
    : highlight === "destructive" ? "text-destructive" : "";
  return (
    <div className="flex items-center justify-between border-b border-border pb-1">
      <dt className="stat-label">{k}</dt>
      <dd className={`font-mono-num font-bold ${cls}`}>{v}</dd>
    </div>
  );
}
